import { ReferralType, ServiceLine } from '@/types';

// Bump when the Affiliate Agreement text changes so acceptance records stay meaningful.
export const TERMS_VERSION = 'v1.0';

export interface ReferralTier {
  key: ReferralType;
  label: string;
  rate: number; // default commission percentage
  blurb: string; // short one-liner for the tier table
  counts: string; // what qualifies for this tier
  notCounts: string; // what does not qualify
  recurring?: boolean;
}

// GRID Affiliate Referral Program commission tiers.
// Commission is a percentage of COLLECTED project revenue.
// GRID never cold-calls — every tier assumes the prospect has agreed to be contacted.
export const REFERRAL_TIERS: ReferralTier[] = [
  {
    key: 'WARM_INTRO',
    label: 'Warm Introduction',
    rate: 10,
    blurb: 'You connect GRID and the prospect directly, and a first call gets booked.',
    counts: 'You directly introduce GRID and the prospect — group email, DM, or in person — and a discovery call is scheduled.',
    notCounts: 'Forwarding a contact without actually introducing both sides, or an intro where no call is booked.',
  },
  {
    key: 'QUALIFIED',
    label: 'Qualified Referral',
    rate: 15,
    blurb: 'An introduced prospect with a confirmed need, budget, and timeline, ready to scope.',
    counts: 'At handoff the prospect has a confirmed need, a budget, and a timeline, and is ready to discuss scope.',
    notCounts: '“Might need something someday” with no budget or timeline attached.',
  },
  {
    key: 'CLOSING_PARTNER',
    label: 'Active Closing Partner',
    rate: 20,
    blurb: 'You stay involved through the process and help move the deal to signature.',
    counts: 'Beyond the intro you keep helping — relaying info, following up, assisting the conversation — until the deal is signed.',
    notCounts: 'A one-time introduction with no further involvement.',
  },
  {
    key: 'ADDITIONAL_SERVICE',
    label: 'Additional Service',
    rate: 10,
    blurb: 'A further service sold to a client you already referred — e.g. AI automation or a bridging platform after the initial website.',
    counts: 'A client you originally referred takes on an additional GRID service beyond their first project. Each additional stage earns 10% of the collected revenue.',
    notCounts: 'The initial project itself (covered by the introduction tiers) or ongoing recurring plans (covered by Recurring Work).',
  },
  {
    key: 'RECURRING',
    label: 'Recurring Work',
    rate: 5,
    blurb: 'Your referral signs an ongoing plan — 5% of each month collected, up to 12 months, on top of any one-time commission.',
    counts: 'The referred client signs a recurring plan (maintenance, support, hosting, audits, ongoing development). Paid on each month GRID collects, up to 12 months, in addition to any one-time project commission.',
    notCounts: 'One-time project work — that is covered by the tiers above.',
    recurring: true,
  },
];

// Optional bonuses paid on top of base commission (recorded manually by an admin).
export interface AffiliateBonus {
  label: string;
  desc: string;
}
export const BONUSES: AffiliateBonus[] = [
  {
    label: 'Large Deal Bonus',
    desc: 'On deals you close as an Active Closing Partner, earn an extra 2% on the collected project revenue above $25,000.',
  },
  {
    label: 'Fast Start Bonus',
    desc: 'A one-time $250 bonus on your first closed referral, for deals over $2,500.',
  },
];

export const getTier = (key?: string): ReferralTier | undefined =>
  REFERRAL_TIERS.find((t) => t.key === key);

export const tierLabel = (key?: string): string => getTier(key)?.label || (key || '—');

// Product/service lines — the upsell ladder a referred client can move along:
// Website (entry) → AI Automation → Bridging Platform. Used to tag referrals
// and CRM leads so the dashboard shows where each client sits.
export const SERVICE_LINES: { key: ServiceLine; label: string }[] = [
  { key: 'WEBSITE', label: 'Website' },
  { key: 'AI_AUTOMATION', label: 'AI Automation' },
  { key: 'BRIDGING', label: 'Bridging Platform' },
  { key: 'OTHER', label: 'Other Service' },
];

export const serviceLineLabel = (key?: string): string =>
  SERVICE_LINES.find((s) => s.key === key)?.label || '—';

// Commission = collected project value * rate%, rounded to cents.
export const computeCommission = (projectValue: number, ratePercent: number): number =>
  Math.round(projectValue * ratePercent) / 100;

// --- Rule-based bonus amounts (mirror of functions/src/bonuses.ts) ---
// These are for UI previews only; the backend recomputes and enforces them.
export const LARGE_DEAL_THRESHOLD = 25000;
export const LARGE_DEAL_RATE = 2; // percent, on revenue above the threshold
export const FAST_START_AMOUNT = 250;
export const FAST_START_MIN_DEAL = 2500;

// Large Deal Bonus: extra 2% of collected revenue above $25k, Active Closing
// Partner deals only. Returns 0 when the deal does not qualify.
export const computeLargeDealBonus = (projectValue: number, referralType?: string): number => {
  if (referralType !== 'CLOSING_PARTNER') return 0;
  if (!(projectValue > LARGE_DEAL_THRESHOLD)) return 0;
  return Math.round((projectValue - LARGE_DEAL_THRESHOLD) * LARGE_DEAL_RATE) / 100;
};

// Fast Start Bonus qualifies on a first closed deal over $2,500.
export const fastStartQualifies = (projectValue: number): boolean => projectValue > FAST_START_MIN_DEAL;
